/**
 * Rentabilidade (Task 16, brief 3.8) — a visão de margem REALIZADA por pedido e
 * por produto, com drill-down obrigatório.
 *
 * Leia isto antes de mexer:
 *
 * 1. **Cada indicador navega até a fonte.** A margem de um pedido abre o pedido
 *    e a conciliação; a margem de um produto abre os lançamentos do produto —
 *    mesmos registros, mesmo histórico (D-16.5/D-03).
 * 2. **A margem reusa o piso de `packages/pricing`.** `belowFloor` é margem
 *    abaixo do piso aprovado — alerta, não número neutro —, e corrigir
 *    reprecificando exige aprovação humana (`needsHumanApproval`): a tela
 *    MARCA isso, nunca aplica preço sozinha.
 * 3. **Sem receita, a margem percentual é indefinida (—), nunca 0.**
 * 4. **Fonte do simulador é declarada.** Taxa do adquirente do simulador
 *    (credencial ausente) é dita na nota — simulador nunca passa por real.
 *
 * Migrado para `.tsx`/JSX real (Cluster E — Financeiro & Sistema, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`): troca mecânica de
 * sintaxe (`createElement` → JSX). Esta tela já estava tokenizada e já usava
 * ícones `lucide-react` no selo de piso de margem antes desta frente — nenhum
 * ícone novo foi necessário, nenhum `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `KpiCard.tsx`.
import React, { type ReactElement } from "react";
import { PageHeader, StatTile } from "../ui/index.js";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck, TrendingUp, type LucideIcon, TriangleAlert } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { FinancialBoundary } from "../components/FinancialBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { SimulatorNote } from "./Conciliacao.js";
import { isFinancialSourceAwaitingContract } from "../financial/dataset.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatPercent } from "../format.js";
import type { FinancialDataset } from "../financial/dataset.js";
import type { RentabilidadeViewProps } from "../financial/view-props.js";
import type { OrderMargin, ProductMargin } from "../api/types.js";

/** Ícone 14px, herda o tom do selo via `currentColor`; decorativo. */
function floorIcon(Icon: LucideIcon): ReactElement {
  return <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />;
}

/**
 * Selo de piso de margem: ícone + texto, nunca só cor (régua §2 / WCAG 1.4.1).
 * Dentro do piso = success/check; abaixo do piso = danger/alerta, marcando
 * quando exige aprovação humana. `data-below-floor`/`data-needs-approval` e o
 * texto exato seguem no wrapper (hooks dos testes preservados).
 */
function floorBadge(belowFloor: boolean, needsHumanApproval: boolean): ReactElement {
  if (!belowFloor) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-pill border border-success bg-surface px-2 py-0.5 text-xs font-semibold text-success"
        data-below-floor="false"
      >
        {floorIcon(CheckCircle2)}
        <span>dentro do piso</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-pill border border-danger bg-surface px-2 py-0.5 text-xs font-semibold text-danger"
      data-below-floor="true"
      data-needs-approval={String(needsHumanApproval)}
    >
      {floorIcon(TriangleAlert)}
      <span>{needsHumanApproval ? "abaixo do piso — exige aprovação humana" : "abaixo do piso"}</span>
    </span>
  );
}

function OrderMarginsTable({ rows }: { rows: OrderMargin[] }): ReactElement {
  return (
    <RecordTable<OrderMargin>
      bare
      caption="Margem realizada por pedido, navegável até o pedido e a conciliação"
      rows={rows}
      idOf={(row) => row.platformOrderId}
      emptyMessage="Nenhum pedido com margem realizada no período."
      columns={[
        {
          key: "order",
          label: "Pedido",
          render: (row) => (
            <Link to={`/pedidos/${row.platformOrderId}`} className="font-semibold text-brand-text hover:underline">
              {row.orderExternalId ?? row.platformOrderId}
            </Link>
          )
        },
        { key: "revenue", label: "Receita", render: (row) => formatBrl(row.revenueCents) },
        { key: "cost", label: "Custo", render: (row) => formatBrl(row.costCents) },
        {
          key: "margin",
          label: "Margem",
          render: (row) => <span data-order-margin={row.marginCents}>{formatBrl(row.marginCents)}</span>
        },
        {
          key: "pct",
          label: "Margem %",
          render: (row) => (row.marginPct === null ? "—" : formatPercent(row.marginPct))
        },
        { key: "floor", label: "Piso", render: (row) => floorBadge(row.belowFloor, row.needsHumanApproval) },
        {
          key: "reconciliation",
          label: "Conciliação",
          render: (row) => (
            <Link to={`/financeiro/conciliacao/recon_${row.platformOrderId}`} className="font-semibold text-brand-text hover:underline">
              abrir
            </Link>
          )
        }
      ]}
    />
  );
}

function ProductMarginsTable({ rows }: { rows: ProductMargin[] }): ReactElement {
  return (
    <RecordTable<ProductMargin>
      bare
      caption="Margem realizada consolidada por produto"
      rows={rows}
      idOf={(row) => row.productId}
      emptyMessage="Nenhum produto com venda registrada no período."
      columns={[
        { key: "product", label: "Produto", render: (row) => row.productName ?? row.productId },
        { key: "orders", label: "Pedidos", render: (row) => String(row.orderCount) },
        { key: "revenue", label: "Receita", render: (row) => formatBrl(row.revenueCents) },
        { key: "cost", label: "Custo", render: (row) => formatBrl(row.costCents) },
        {
          key: "margin",
          label: "Margem",
          render: (row) => <span data-product-margin={row.marginCents}>{formatBrl(row.marginCents)}</span>
        },
        { key: "pct", label: "Margem %", render: (row) => (row.marginPct === null ? "—" : formatPercent(row.marginPct)) },
        { key: "floor", label: "Piso", render: (row) => floorBadge(row.belowFloor, row.needsHumanApproval) }
      ]}
    />
  );
}

export function RentabilidadeView({ financial, orderId = null, productId = null }: RentabilidadeViewProps): ReactElement {
  if (isFinancialSourceAwaitingContract(financial)) {
    return (
      <div className="page page--rentabilidade flex flex-col gap-6" data-page="rentabilidade">
        <PageHeader title="Rentabilidade" />
        <StatusPanel
          kind="awaiting_source"
          title={`Rentabilidade ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/platform/financial/summary não respondeu neste ambiente. A rentabilidade aparece quando a rota for publicada — exibir 'margem zero' diria, falsamente, que a operação não teve resultado."
        />
      </div>
    );
  }
  const { summary } = financial;
  const orderMargins = orderId ? financial.orderMargins.filter((row) => row.platformOrderId === orderId) : financial.orderMargins;
  const productMargins = productId ? financial.productMargins.filter((row) => row.productId === productId) : financial.productMargins;
  return (
    <div className="page page--rentabilidade flex flex-col gap-6" data-page="rentabilidade">
      <PageHeader
        title="Rentabilidade"
        description="Margem realizada por pedido e por produto, comparada ao piso aprovado."
      />
      <p className="sr-only" data-margin-total={summary.marginCents} data-below-floor-orders={summary.belowFloorOrders}>
        {`Margem do período: ${formatBrl(summary.marginCents)}` +
          (summary.marginPct === null ? "" : ` (${formatPercent(summary.marginPct)})`) +
          ` · piso aprovado ${formatPercent(summary.minMarginPct)} · ${summary.belowFloorOrders} pedido(s) abaixo do piso.`}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatTile label="Margem do período" value={formatBrl(summary.marginCents)} icon={TrendingUp} hint={summary.marginPct === null ? undefined : formatPercent(summary.marginPct)} />
        <StatTile label="Piso aprovado" value={formatPercent(summary.minMarginPct)} icon={ShieldCheck} />
        <StatTile label="Pedidos abaixo do piso" value={summary.belowFloorOrders} icon={TriangleAlert} />
      </div>
      <SimulatorNote financial={financial} />
      <p className="page__note text-sm text-muted">
        Margem REALIZADA (receita menos taxa, comissão e custo), não a recomendada do Price Engine. Abaixo do piso é
        alerta de gestão; corrigir reprecificando passa por aprovação humana — a tela não aplica preço sozinha.
      </p>
      <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="margem-pedido">
        <h3 className="text-lg font-extrabold text-ink">Margem por pedido</h3>
        <OrderMarginsTable rows={orderMargins} />
      </section>
      <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="margem-produto">
        <h3 className="text-lg font-extrabold text-ink">Margem por produto</h3>
        <ProductMarginsTable rows={productMargins} />
      </section>
    </div>
  );
}

export function Rentabilidade(): ReactElement {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");
  const productId = searchParams.get("product");
  return (
    <FinancialBoundary>
      {(financial: FinancialDataset) => <RentabilidadeView financial={financial} orderId={orderId} productId={productId} />}
    </FinancialBoundary>
  );
}
